import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    MdArrowBack,
    MdCallReceived,
    MdCallMade,
    MdPerson,
    MdStorefront
} from 'react-icons/md';
import Loader from '../components/common/Loader';
import "react-datepicker/dist/react-datepicker.css";
import './AddPayment.css';
import './AddGenerateRequest.css';


import axios from 'axios';
import {
    GET_RECEIPT_BY_ID_API,
    GET_PO_SUMMARY_API
} from '../config/API';
import { showErrorToast } from '../utils/toastUtils';

const ViewPayment = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    const [formData, setFormData] = useState({
        transactionType: 'payment',
        partnerType: 'vendor',
        partnerName: '',
        projectName: '',
        poNumber: '',
        payment_remark: '',
        payment_mode: '',
        particular: ''
    });

    const [receiptPos, setReceiptPos] = useState([]);
    const [poDetailsMap, setPoDetailsMap] = useState({});

    const [poSummary, setPoSummary] = useState({
        poValue: 0,
        totalPaid: 0,
        outstanding: 0
    });
    
    const fetchPoSummaries = async (poIds) => {
        if (!poIds || poIds.length === 0) return;

        try {
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_PO_SUMMARY_API(poIds.join(',')), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status && response.data.data) {
                const summaries = response.data.data;
                const newDetailsMap = {};

                summaries.forEach(data => {
                    newDetailsMap[data.id] = {
                        poValue: parseFloat(data.poAmount || 0),
                        totalPaid: parseFloat(data.paidAmount || 0),
                        outstanding: parseFloat(data.outstandingAmount || 0),
                        poNumber: data.poNumber || `PO #${data.id}`
                    };
                });

                setPoDetailsMap(prev => ({ ...prev, ...newDetailsMap }));
            }
        } catch (error) {
            console.error(`Error fetching PO summaries for IDs ${poIds}:`, error);
        }
    };

    // Calculate aggregate summary
    useEffect(() => {
        let aggregatePoValue = 0;
        let aggregateTotalPaid = 0;
        let aggregateOutstanding = 0;

        receiptPos.forEach(rp => {
            const details = poDetailsMap[rp.poId];
            if (details) {
                aggregatePoValue += details.poValue;
                aggregateTotalPaid += (details.totalPaid - parseFloat(rp.amount || 0)); // Paid BEFORE this receipt
                aggregateOutstanding += (details.poValue - details.totalPaid); // Current Outstanding AFTER this receipt
            }
        });

        setPoSummary({
            poValue: aggregatePoValue,
            totalPaid: aggregateTotalPaid,
            outstanding: aggregateOutstanding
        });
    }, [poDetailsMap, receiptPos]);

    useEffect(() => {
        const fetchPaymentData = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const response = await axios.get(GET_RECEIPT_BY_ID_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status && response.data.data) {
                    const data = response.data.data;

                    setFormData({
                        transactionType: 'receipt',
                        partnerType: 'vendor',
                        partnerName: data.Vendor?.vendorName || data.PurchaseOrder?.RequestVendor?.Vendor?.vendorName || 'N/A',
                        projectName: data.ProjectMaster?.projectName || data.PurchaseOrder?.ProjectMaster?.projectName || 'N/A',
                        amount: data.totalAmount || data.amount || '0',
                        date: data.date ? new Date(data.date).toLocaleDateString('en-GB') : 'N/A',
                        particular: data.remark || '',
                        payment_remark: data.payment_remark || 'N/A',
                        payment_mode: data.payment_mode || 'N/A'
                    });

                    if (data.ReceiptPos && Array.isArray(data.ReceiptPos)) {
                        setReceiptPos(data.ReceiptPos);
                        const poIds = data.ReceiptPos.map(rp => rp.poId);
                        fetchPoSummaries(poIds);
                    } else if (data.poId) {
                        // Legacy support
                        setReceiptPos([{ 
                            poId: data.poId, 
                            amount: data.amount,
                            PurchaseOrder: data.PurchaseOrder 
                        }]);
                        fetchPoSummaries([data.poId]);
                    }
                }
            } catch (error) {
                console.error('Error fetching payment details:', error);
                showErrorToast('Failed to load payment details');
            } finally {
                setLoading(false);
            }
        };

        fetchPaymentData();
    }, [id]);


    if (loading) {
        return <Loader />;
    }

    return (
        <div className="add-po-page add-payment-page view-payment-page">
            <div className="page-header">
                <button onClick={() => navigate('/payment-management')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>View Payment Details</h2>
            </div>

            <div className="project-form payment-form">
                <div className="form-section">
                    <h3>Transaction Details</h3>


                    <div className="form-grid">
                        <div className="form-group">
                            <label>Date</label>
                            <input type="text" className="form-control" value={formData.date} disabled />
                        </div>

                        <div className="form-group">
                            <label>Site / Project</label>
                            <input type="text" className="form-control" value={formData.projectName} disabled />
                        </div>

                        <div className="form-group">
                            <label>Vendor Name</label>
                            <input type="text" className="form-control" value={formData.partnerName} disabled />
                        </div>

                        {receiptPos.length > 0 && (
                            <div className="form-group full-width" style={{ marginTop: '1rem' }}>
                                <label style={{ marginBottom: '0.75rem', display: 'block' }}>Purchase Orders (PO) Allocation</label>
                                <div className="po-selection-table-container">
                                    <table className="items-table po-summary-table">
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: 'left' }}>PO Number</th>
                                                <th className="text-right" style={{ width: '130px' }}>PO Amount</th>
                                                <th className="text-right" style={{ width: '120px' }}>Paid (Prev)</th>
                                                <th className="text-right" style={{ width: '130px' }}>Allocation</th>
                                                <th className="text-right" style={{ width: '140px' }}>Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {receiptPos.map((rp) => {
                                                const details = poDetailsMap[rp.poId];
                                                const allocation = parseFloat(rp.amount || 0);
                                                const paidPrev = (details?.totalPaid || 0) - allocation;
                                                const remaining = (details?.poValue || 0) - (details?.totalPaid || 0);
                                                
                                                return (
                                                    <tr key={rp.poId}>
                                                        <td style={{ textAlign: 'left', fontWeight: '500' }}>{details?.poNumber || rp.PurchaseOrder?.poNumber || `PO #${rp.poId}`}</td>
                                                        <td className="text-right">₹{details?.poValue?.toLocaleString() || '...'}</td>
                                                        <td className="text-right" style={{ color: '#64748b' }}>₹{paidPrev?.toLocaleString() || '...'}</td>
                                                        <td className="text-right" style={{ fontWeight: '700', color: '#059669' }}>
                                                            ₹{allocation.toLocaleString()}
                                                        </td>
                                                        <td className="text-right" style={{ fontWeight: '700', color: remaining > 0 ? '#dc2626' : '#059669' }}>
                                                            ₹{remaining.toLocaleString()}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="summary-card-horizontal" style={{
                                    marginTop: '1.5rem',
                                    background: '#f8fafc',
                                    padding: '1.25rem',
                                    borderRadius: '12px',
                                    border: '1px solid #e2e8f0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    gap: '1.5rem'
                                }}>
                                    <div className="summary-section">
                                        <div className="summary-label" style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.025em' }}>POs Involved</div>
                                        <div className="summary-value" style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1e293b' }}>{receiptPos.length}</div>
                                    </div>
                                    <div className="summary-section">
                                        <div className="summary-label" style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Aggregate PO Value</div>
                                        <div className="summary-value" style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1e293b' }}>₹{poSummary.poValue.toLocaleString()}</div>
                                    </div>
                                    <div className="summary-section">
                                        <div className="summary-label" style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Aggregate Paid (Prev)</div>
                                        <div className="summary-value" style={{ fontSize: '1.1rem', fontWeight: '700', color: '#64748b' }}>₹{poSummary.totalPaid.toLocaleString()}</div>
                                    </div>
                                    <div className="summary-section">
                                        <div className="summary-label" style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Current Allocation</div>
                                        <div className="summary-value" style={{ fontSize: '1.1rem', fontWeight: '700', color: '#059669' }}>₹{parseFloat(formData.amount || 0).toLocaleString()}</div>
                                    </div>
                                    <div className="summary-section">
                                        <div className="summary-label" style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Net Outstanding</div>
                                        <div className="summary-value" style={{ fontSize: '1.25rem', fontWeight: '800', color: '#dc2626' }}>₹{poSummary.outstanding.toLocaleString()}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <label>Amount</label>
                            <div className="form-control-static" style={{
                                padding: '0.625rem 0.75rem',
                                background: '#f9fafb',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontWeight: '700',
                                color: '#059669'
                            }}>
                                ₹{parseFloat(formData.amount || 0).toLocaleString()}
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Payment Mode</label>
                            <input
                                type="text"
                                className="form-control"
                                value={formData.payment_mode}
                                disabled
                                style={{ background: '#f9fafb' }}
                            />
                        </div>

                        {formData.payment_mode === 'Cheque' && (
                            <div className="form-group">
                                <label>Cheque Number</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={formData.payment_remark}
                                    disabled
                                    style={{ background: '#f9fafb' }}
                                />
                            </div>
                        )}

                        <div className="form-group full-width">
                            <label>Particular / Notes</label>
                            <textarea
                                className="form-control"
                                value={formData.particular}
                                disabled
                                rows="3"
                                style={{ background: '#f9fafb' }}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/payment-management')} className="cancel-btn">
                        Back to List
                    </button>
                    <button type="button" onClick={() => navigate(`/payment-management/edit/${id}`)} className="save-btn">
                        Edit Payment
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ViewPayment;
